import { createFileRoute } from '@tanstack/react-router'
import { PosthogStatistics } from '../components/PosthogStatistics'
import { Statistics } from '../components/Statistics'
import type { FCWithChildren } from '../types'

const AboutSection: FCWithChildren<{ className?: string }> = ({
  children,
  className,
}) => <div className={`mb-4 ${className ?? ''}`}>{children}</div>

const AboutPage = () => {
  return (
    <div className="mt-6 flex-col sm:w-full">
      <AboutSection>
        <h4 className="mb-2 text-xl font-bold text-gray-800">About SID</h4>
        <p className="mb-2">
          The Seed Information Database (SID) is now hosted by the{' '}
          <a
            className="text-serblue-light underline"
            href="https://www.ser.org/default.aspx"
          >
            Society for Ecological Restoration
          </a>{' '}
          (SER) in collaboration with the{' '}
          <a
            className="text-serblue-light underline"
            href="https://www.kew.org/"
          >
            Royal Botanic Gardens Kew
          </a>{' '}
          (RBGK). SID is a compilation of seed biological trait data, with
          records derived from measurements and observations on seed collections
          held in Royal Botanic Garden Kew’s Millennium Seed Bank and from other
          unpublished and published sources, through donation or abstraction.
        </p>
        <p className="mb-2">
          Since 2003 Kew has placed the data accumulated in the public domain
          via its website, for the benefit of the wider scientific conservation
          and restoration community. It provided user-friendly access to
          information on seed weight, storage behaviour, germination
          requirements, and other traits for more than 50.000 plant taxa.
        </p>
        <p className="mb-2">
          This wealth of information has supported everyday operations in
          scientific research, seed use in ecological restoration, seed
          collection and propagation, and teaching. The Society for Ecological
          Restoration, through its thematic section,{' '}
          <a
            className="text-serblue-light underline"
            href="https://ser-insr.org/"
          >
            the International Network for Seed-based Restoration (INSR)
          </a>{' '}
          is delighted to have reached an agreement with RBGK to host the
          database and allow for seed trait information to be readily and easily
          accessible to all.
        </p>
        <p className="mb-2">
          The refreshed interface was developed by{' '}
          <a
            className="text-serblue-light underline"
            href="http://fieldworklabs.xyz/"
          >
            Fieldwork Labs
          </a>
          , Fiona Hay, and Simone Pedrini with the assistance of Robert Turner
          and Eduardo Toledo from RBGK.
        </p>
        <p>
          Support for the database transfer and interface development was
          provided by the{' '}
          <a
            className="text-serblue-light underline"
            href="https://arc-cmsr.org/index.php/en/"
          >
            Centre for Mine Site Restoration (CMSR)
          </a>{' '}
          at Curtin University, Western Australia and is now managed by INSR.
          All data contained in SID are publicly available under the terms of
          the Creative Commons CC BY 2.0.
        </p>
      </AboutSection>
      <AboutSection>
        <Statistics />
        <PosthogStatistics />
      </AboutSection>
      <AboutSection>
        <h4 className="mb-2 text-xl font-bold text-gray-800">How to cite</h4>
        <p>
          Please cite this database as: SER, INSR, RBGK, Seed Information
          Database (SID), {new Date().getFullYear()}
        </p>
        <p>
          Please include this database within your Acknowledgements,
          Bibliography or References as: Society for Ecological Restoration,
          International Network for Seed Based Restoration and Royal Botanic
          Gardens Kew. (2023) Seed Information Database (SID). Available from:
          https://ser-sid.org/ (
          {new Date().toLocaleString('default', { month: 'long' })},{' '}
          {new Date().getFullYear()})
        </p>
      </AboutSection>
      <AboutSection className="/12 mx-auto mt-6 flex justify-between align-middle xl:w-10/12">
        {/* SER, KEW, INSR, Curtin,  CMSR */}
        <img width="200" height="100" src="/images/SER.png" alt="SER Logo" />
        <img width="180" height="100" src="/images/RBGK.png" alt="Kew Logo" />
        <img width="200" height="100" src="/images/INSR.png" alt="INSR Logo" />
        <img
          width="250"
          height="100"
          src="/images/CURTIN.png"
          alt="Curtin University Logo"
        />
        <img width="260" height="100" src="/images/CMSR.png" alt="CMSR Logo" />
      </AboutSection>
    </div>
  )
}

export const Route = createFileRoute('/about')({
  component: AboutPage,
})
